import React, { FC } from "react"
import { useUiContext } from "components/providers/uiProvider"
import { Suggestions } from "components/dashboard/suggestions"
import { GridContent } from "components/dashboard/grid/gridContent"
import { Dashboard } from "domainModels/dashboard"
import { useIsDashboardOwner } from "reactQuery/dashboard"
import { useSelectPublicWidgets, useSelectWidgets } from "reactQuery/widget"
import { useSelectGroups } from "reactQuery/group"
import { Fork } from "./fork"
import { useUser } from "lib/supabase/auth"
import { useUpdateLayout } from "reactQuery/layout"
import { Link } from "components/core/link"
import { Button } from "components/core/button"
import { XMarkIcon } from "@heroicons/react/24/outline"
import classNames from "classnames"
import { Divider } from "components/core/divider"

interface Props {
  dashboard: Dashboard
}

export const Grid: FC<Props> = ({ dashboard }) => {
  const { user } = useUser()
  const {
    value: { showSuggestions },
    update,
  } = useUiContext()
  const isDashboardOwner = useIsDashboardOwner()
  const { data: widgets, isLoading: isLoadingWidgets } = useSelectWidgets(dashboard.id)
  const { data: publicWidgets, isLoading: isLoadingPublicWidgets } = useSelectPublicWidgets()
  const { data: groups, isLoading: isLoadingGroups } = useSelectGroups(dashboard.id)
  const { mutateAsync: updateLayout } = useUpdateLayout()
  const isLoading = isLoadingWidgets || isLoadingGroups || isLoadingPublicWidgets

  return (
    <>
      {!isDashboardOwner && <Fork dashboard={dashboard} />}
      {isDashboardOwner && !user && (
        <div className="text-sm px-4 sm:px-6 lg:px-12 pt-4">
          <Link to="/auth/login">Sign in</Link> to permanently persist your dashboard
          <div className="mt-4">
            <Divider />
          </div>
        </div>
      )}
      <div className="px-4 md:px-0 lg:px-8">
        {dashboard && widgets && publicWidgets && !isLoading && (
          <GridContent
            dashboard={dashboard}
            isPrivate={dashboard.is_private}
            widgets={widgets}
            publicWidgets={publicWidgets}
            groups={groups}
            updateLayout={updateLayout}
          />
        )}

        <div
          className={classNames(
            "fixed z-10 bottom-0 transition-transforn duration-300 translate-y-full flex flex-col justify-between inset-x-0 border-t border-black/10 dark:border-indigo-300/10",
            {
              "translate-y-0": showSuggestions,
            }
          )}
        >
          <div className="bg-white/70 dark:bg-indigo-900/20 backdrop-blur">
            <div className="absolute z-10 -top-2.5 right-0 p-3.5">
              <Button
                type="button"
                variant="naked"
                aria-label="Hide suggestions"
                onClick={() => update({ suggestionInfo: undefined, showSuggestions: false })}
                className="-mr-3"
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>
            <div className="my-3 px-5">
              <h2 className="text-xs uppercase leading-6 font-medium text-gray-600 dark:text-gray-400">Suggestions</h2>
            </div>

            <div className="overflow-x-scroll px-6 pb-4">
              <Suggestions dashboard={dashboard} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
